/**
 * The visit registry (O10, CASE-027): which area draws each object's records,
 * and, until that area ships, which legacy page an address falls back to.
 *
 * A source act only calls `visit(address)`; it never knows which area draws the
 * target. Each area registers its objects in the one table (`targets.ts`), and
 * its PR replaces its fallback rows with registered ones.
 */
import type { ComponentType, ReactNode, SVGProps } from "react";
import type { Box } from "@/components/ui/box.schema";

import { MAX_TRAIL, parseLevel, type VisitLevel } from "./address";

/** The shell a page belongs to. A legacy page differs by shell; a visit doesn't. */
export type VisitShell = "admin" | "adminTeaching" | "instructor" | "student";

/** The area that owns an object's records (the joint delivery split), or the specimen's fixtures. */
export type VisitOwner = "A2" | "A3" | "A5" | "A6" | "A7" | "specimen";

/** Presentation groups over the same native boxes; no read, draft or action owner. */
export interface VisitSection {
  id: string;
  label: string;
  icon?: ComponentType<Omit<SVGProps<SVGSVGElement>, "ref">>;
  summary?: string;
  boxIds: string[];
  defaultOpen?: boolean;
}

/** What a level reads as, through the person's own queries. */
export type VisitResolution =
  | { status: "loading"; entryFocus?: string }
  /** The person may not see it, or it doesn't exist: the same answer, naming nothing (R1). */
  | { status: "refused" }
  | {
      status: "ready";
      /** Names the subject once, on the frame's heading ("Layla Karimi · Faculty"). */
      subject: string;
      /** Optional control to focus on entry, scoped inside the active visit. */
      entryFocus?: string;
      /** Flush the current native draft before controlled Return/Back. */
      beforeLeave?: () => Promise<unknown>;
      /** The record's boxes, in the record's own order. */
      boxes: Box[];
      /** Native component controllers may publish models while the host alone draws them. */
      controller?: ReactNode;
      /** Ordered donor disclosures; every ungrouped box remains visible. */
      sections?: VisitSection[];
      /** The boxes that start open: the one the address names. The rest rest as one line with Open. */
      opens?: string[];
    };

interface TargetBase {
  object: string;
  owner: VisitOwner;
  /** What may follow `/`: the jobs this object opens, or "record" when it names a second record (`enroll:<person>/<course>`). */
  jobs: readonly string[] | "record";
  /** The word for a level whose subject this tab hasn't read: a pasted onward link's earlier level ("Person"). */
  noun: string;
}

export interface RegisteredTarget extends TargetBase {
  kind: "registered";
  /** The boxes that take this visit when no opener is known (a pasted link). The first one drawn takes it, else the page's first box. */
  hosts: readonly string[];
  /**
   * "course" when the level's id names a course (`material:<course>/<entry>`).
   * On a course's own page such a level must name that course (#1393 R33): a
   * record of another course is refused before anything is read, even when
   * the member could read both.
   */
  scope?: "course";
  /**
   * Reads the level's boxes. The host calls every registered target's hook on
   * every render, with null when the visit is elsewhere, so hook order never
   * changes. Return null for null.
   */
  useBoxes(level: VisitLevel | null): VisitResolution | null;
}

export interface FallbackTarget extends TargetBase {
  kind: "fallback";
  /** The legacy page the address opens until its area registers it, or null where this shell has none. */
  legacy(level: VisitLevel, shell: VisitShell): string | null;
}

export type VisitTarget = RegisteredTarget | FallbackTarget;

export type VisitPlan =
  | { kind: "none" }
  | { kind: "refused" }
  /** The one listed exception to "no act changes the page path": a named deviation until its area ships. */
  | { kind: "fallback"; target: FallbackTarget; path: string }
  | { kind: "visit"; target: RegisteredTarget; level: VisitLevel };

function accepts(target: VisitTarget, level: VisitLevel): boolean {
  if (target.jobs === "record") return level.job !== null;
  return level.job === null || target.jobs.includes(level.job);
}

/** The target and level one address names, or null when no area owns it. */
export function targetOf(targets: readonly VisitTarget[], raw: string): { target: VisitTarget; level: VisitLevel } | null {
  const level = parseLevel(raw);
  const target = level && targets.find((candidate) => candidate.object === level.object && accepts(candidate, level));
  return level && target ? { target, level } : null;
}

/**
 * What the trail draws. Every level must be an address some area owns; the last
 * one is drawn. Anything else is refused whole and names nothing (R1).
 */
export function planVisit(trail: readonly string[], targets: readonly VisitTarget[], shell: VisitShell | null, course: string | null = null): VisitPlan {
  if (trail.length === 0) return { kind: "none" };
  if (trail.length > MAX_TRAIL) return { kind: "refused" };
  const levels = trail.map((raw) => targetOf(targets, raw));
  const last = levels[levels.length - 1];
  if (!last || levels.some((level) => level === null)) return { kind: "refused" };
  if (course !== null && levels.some((level) => level!.target.kind === "registered" && level!.target.scope === "course" && level!.level.id !== course)) return { kind: "refused" };
  if (last.target.kind === "registered") return { kind: "visit", target: last.target, level: last.level };
  const path = shell ? last.target.legacy(last.level, shell) : null;
  return path ? { kind: "fallback", target: last.target, path } : { kind: "refused" };
}

/** The word an earlier trail level shows when this tab never read its subject. */
export function nounOf(targets: readonly VisitTarget[], raw: string): string | null {
  return targetOf(targets, raw)?.target.noun ?? null;
}

/** The course whose workspace a path is (`/{shell}/courses/<course>`), else null; the registrar's record page is not a workspace. */
export function courseOf(pathname: string): string | null {
  const match = /^\/(?:instructor|admin\/teaching|student)\/courses\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/** The shell a path belongs to, as the app shell reads it; null outside the seats (the specimen). */
export function shellOf(pathname: string): VisitShell | null {
  if (pathname === "/student" || pathname.startsWith("/student/")) return "student";
  if (pathname === "/admin/teaching" || pathname.startsWith("/admin/teaching/")) return "adminTeaching";
  if (pathname === "/instructor" || pathname.startsWith("/instructor/")) return "instructor";
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return "admin";
  return null;
}
